// src/components/Navbar.tsx
"use client";

import { useState } from "react";
import { Button } from "@heroui/react";
import { Menu, X, LogOut, Settings } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { WalletAuthButton } from "./WalletAuthButton";
import { useConnection } from "@/contexts/connection-context";
import { useGetCampaignUser } from "@/hooks/challenges";
import { useGetSocialUser } from "@/hooks/social-pay";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { ProfileModal } from "./ProfileModal";
import { BlockiesAvatar } from "./BlockiesAvatar";

export function RhunaNavbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isAuthenticated, walletAddress, disconnectWallet } = useConnection();
  const { data: campaignUser } = useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID, isAuthenticated);
  const { data: socialUser } = useGetSocialUser();

  // Get user display info with proper priority
  const getUserDisplayInfo = () => {
    // Priority: Rhuna services (campaignUser.name) → Twitter username → truncated address
    if (campaignUser?.name) {
      return {
        name: campaignUser.name,
        photoUrl: campaignUser.photoUrl
      };
    }
    if (socialUser?.twitterMetadata?.username) {
      return {
        name: socialUser.twitterMetadata.username,
        photoUrl: socialUser.twitterMetadata.profilePicture
      };
    }
    if (socialUser?.discordMetadata?.username) {
      return {
        name: socialUser.discordMetadata.username,
        photoUrl: socialUser.discordMetadata.avatar ? `https://cdn.discordapp.com/avatars/${socialUser.discordMetadata.userId}/${socialUser.discordMetadata.avatar}.png` : null
      };
    }
    if (walletAddress) {
      return {
        name: `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`,
        photoUrl: null
      };
    }
    return null;
  };

  const userInfo = getUserDisplayInfo();

  const handleSignOut = () => {
    disconnectWallet();
  };

  return (
    <nav className="border-b border-gray-800/50 px-4 py-3" style={{ backgroundColor: "#0C1113" }}>
      <div className="flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <img src="https://pulsarmoney.b-cdn.net/rhuna/logo.png" alt="RHUNA" className="h-8 w-auto" />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          <Link href="/" className="text-gray-300 hover:text-white transition-colors">
            Home
          </Link>
          <Link href="/quests" className="text-gray-300 hover:text-white transition-colors">
            Quests
          </Link>
          <Link href="/daily-lottery" className="text-gray-300 hover:text-white transition-colors">
            Rhuna Forge
          </Link>
          <Link href="/referrals" className="text-gray-300 hover:text-white transition-colors">
            Referrals
          </Link>
          <Link href="/leaderboard" className="text-gray-300 hover:text-white transition-colors">
            Leaderboard
          </Link>
        </div>

        {/* Right side - User display or Connect button (Desktop only) */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated && userInfo ? (
            <div className="flex items-center gap-3">
              {/* User info */}
              <div className="flex items-center gap-2">
                {userInfo.photoUrl ? (
                  <Image
                    src={userInfo.photoUrl}
                    alt="Profile"
                    width={32}
                    height={32}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {userInfo.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-white text-base font-medium">{userInfo.name}</span>
                  <span className="text-gray-400 text-sm">
                    #{campaignUser?.rank || '--'}
                  </span>
                </div>
              </div>
              
              {/* Settings button */}
              <Button
                isIconOnly
                variant="light"
                className="text-gray-300 hover:text-white"
                onPress={() => setIsProfileModalOpen(true)}
              >
                <Settings size={20} />
              </Button>
              
              {/* Sign out button */}
              <Button
                isIconOnly
                variant="light"
                className="text-gray-300 hover:text-red-400"
                onPress={handleSignOut}
              >
                <LogOut size={20} />
              </Button>
            </div>
          ) : (
            <WalletAuthButton variant="default" />
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button isIconOnly variant="light" className="md:hidden text-white" onPress={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

       {/* Mobile Navigation */}
       {isMenuOpen && (
         <div className="md:hidden mt-4 pb-4 border-t border-gray-800/50 pt-4">
           <div className="flex flex-col space-y-3">
             <Link 
               href="/" 
               className="text-gray-300 hover:text-white transition-colors"
               onClick={() => setIsMenuOpen(false)}
             >
               Home
             </Link>
             <Link 
               href="/quests" 
               className="text-gray-300 hover:text-white transition-colors"
               onClick={() => setIsMenuOpen(false)}
             >
               Quests
             </Link>
            <Link 
              href="/daily-lottery" 
              className="text-gray-300 hover:text-white transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Rhuna Forge
            </Link>
             <Link 
               href="/referrals" 
               className="text-gray-300 hover:text-white transition-colors"
               onClick={() => setIsMenuOpen(false)}
             >
               Referrals
             </Link>
             <Link 
               href="/leaderboard" 
               className="text-gray-300 hover:text-white transition-colors"
               onClick={() => setIsMenuOpen(false)}
             >
               Leaderboard
             </Link>
             
             {/* Mobile User Actions - Only show when authenticated */}
             {isAuthenticated && userInfo && (
               <div className="mt-4 pt-4 border-t border-gray-800/50">
                 <div className="flex items-center gap-3 mb-3">
                   <div className="w-12 h-12 rounded-full overflow-hidden">
                     <BlockiesAvatar
                       seed={walletAddress || userInfo.name || 'user'}
                       size={12}
                       scale={4}
                     />
                   </div>
                   <div className="flex-1 min-w-0">
                     <p className="text-white text-base font-medium truncate">{userInfo.name}</p>
                     <p className="text-gray-400 text-sm">
                       #{campaignUser?.rank || '--'}
                     </p>
                   </div>
                   <button
                     onClick={() => {
                       handleSignOut();
                       setIsMenuOpen(false);
                     }}
                     className="text-gray-300 hover:text-white transition-colors"
                   >
                     <LogOut className="w-4 h-4" />
                   </button>
                 </div>
                 <button
                   onClick={() => {
                     setIsProfileModalOpen(true);
                     setIsMenuOpen(false);
                   }}
                   className="flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:bg-gray-800 hover:text-white w-full text-left"
                   style={{ color: "#99A1AF" }}
                 >
                   <Settings className="mr-3 h-5 w-5" />
                   Settings
                 </button>
               </div>
             )}
           </div>
         </div>
       )}

       {/* Profile Modal */}
       <ProfileModal
         isOpen={isProfileModalOpen}
         onClose={() => setIsProfileModalOpen(false)}
       />
    </nav>
  );
}
